import { FADE_IN_KEYFRAMES } from '@modules/common/constants';
import { WelcomeMessage } from '@modules/home/components/WelcomeMessage';
import { AskOracleInput } from '@modules/oracle/components/AskOracleInput';
import { OracleSearchSuggestions } from '@modules/oracle/components/OracleSearchSuggestions';
import { WisdomLogoIcon } from '@modules/oracle/components/WisdomLogoIcon';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { trackWisdomEvents } from '@modules/oracle/utils/trackWisdomEvents';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { lime } from '@mui/material/colors';
import { useLocation } from 'react-router-dom';
import { useMeasure } from 'react-use';
import { useGetUser } from 'v2/core/services/user';

export function OracleEmptySearchViewV1() {
  const { firstName } = useGetUser();

  const location = useLocation();

  const sessionID = useOracleSearchStore(({ data }) => data.sessionID);

  const [containerRef, { width: containerWidth }] = useMeasure();

  const showTwoRowsLayout = containerWidth < 920;

  const isRedirectedFromOnboarding = location.state?.isRedirectedFromOnboarding;

  return (
    <Stack
      component={Box}
      sx={{
        pt: 4,
        height: '100%',
        width: '100%',
      }}
      ref={containerRef}
    >
      <Stack direction="column" justifyContent="space-between" flex={1}>
        <WelcomeMessage
          shouldHideFirstName
          containerProps={{
            direction: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            px: 5,
          }}
        />
        <Stack
          direction={showTwoRowsLayout ? 'column' : 'row'}
          justifyContent={showTwoRowsLayout ? 'center' : 'space-between'}
          spacing={6}
          mx="auto"
          maxWidth="1024px"
          mt={-10}
          px={2.5}
        >
          <Stack
            direction="column"
            spacing={2}
            maxWidth={showTwoRowsLayout ? '600px' : '320px'}
            alignItems="flex-start"
            sx={{
              opacity: isRedirectedFromOnboarding ? 0 : 1,
              animation: isRedirectedFromOnboarding
                ? `${FADE_IN_KEYFRAMES} 0.7s ease-in-out forwards`
                : 'none',
              animationDelay: '0.3s',
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <WisdomLogoIcon sx={{ fontSize: '66px' }} />
              <Chip
                size="small"
                label="Beta"
                sx={{
                  backgroundColor: lime[500],
                  color: 'black',
                  position: 'absolute',
                  top: -5,
                  right: -35,
                }}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: '43px',
                lineHeight: '52px',
                background:
                  'linear-gradient(106deg, #B206CE 30.9%, #7357F6 62.92%, #83A9F3 118.3%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
              fontWeight={600}
            >
              Hey {firstName}
            </Typography>
            <Typography variant="h5" color="text.secondary">
              What would you like to learn from your users' feedback?
            </Typography>
          </Stack>
          <Box
            pt={showTwoRowsLayout ? 0 : 11}
            maxWidth="600px"
            minWidth="500px"
            sx={{
              opacity: isRedirectedFromOnboarding ? 0 : 1,
              animation: isRedirectedFromOnboarding
                ? `${FADE_IN_KEYFRAMES} 0.7s ease-in-out forwards`
                : 'none',
              animationDelay: '1s',
            }}
          >
            <OracleSearchSuggestions />
          </Box>
        </Stack>
        <AskOracleInput
          containerSxProps={{
            pb: 1,
            maxWidth: '1024px',
            mx: 'auto',
            px: 2.5,
            opacity: isRedirectedFromOnboarding ? 0 : 1,
            animation: isRedirectedFromOnboarding
              ? `${FADE_IN_KEYFRAMES} 0.7s ease-in-out forwards`
              : 'none',
            animationDelay: '1.5s',
          }}
          onSubmit={(searchText: string) => {
            trackWisdomEvents({
              eventName: 'Wisdom - Input Submitted',
              searchText,
              sessionID,
              threadPosition: 0,
            });
          }}
        />
      </Stack>
    </Stack>
  );
}
